import type { ReadonlySignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { useMemo } from 'react';
import { useContainer } from 'src/features/ioc';
import type { FeatureFlag } from 'src/lib/featureFlags/FeatureFlag';

/**
 * Returns a reactive signal for the given feature flag.
 * The caller is responsible for reading .value and opting into signal reactivity.
 *
 * @example
 * function MyComponent() {
 *     useSignals();
 *     const enableFeature = useFeatureFlagSignal(enableFeatureX);
 *     return <div>{enableFeature.value ? 'Enabled' : 'Disabled'}</div>;
 * }
 */
export const useFeatureFlagSignal = <T>(flag: FeatureFlag<T>): ReadonlySignal<T> => {
    const container = useContainer();
    const service = useMemo(() => container.get('FeatureFlagService'), [container]);
    return service.getSignal(flag);
};

/**
 * Just a wrapper around useFeatureFlag leaving in place for in flight PRs
 * @deprecated Use useFeatureFlag instead
 */
export const useFeatureFlagValue = <T>(flag: FeatureFlag<T>): T => {
    return useFeatureFlag(flag);
};

/**
 * Returns the current value of a feature flag, re-rendering when it changes.
 * This is the recommended hook for most components that just need the flag value.
 *
 * @example
 * function MyComponent() {
 *     const enableFeature = useFeatureFlag(enableFeatureX);
 *     return <div>{enableFeature ? 'Enabled' : 'Disabled'}</div>;
 * }
 */
export const useFeatureFlag = <T>(flag: FeatureFlag<T>): T => {
    useSignals();
    const container = useContainer();
    const service = useMemo(() => container.get('FeatureFlagService'), [container]);
    return service.getSignal(flag).value;
};
